import {
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  Drawer,
  Fab,
  IconButton,
  Input,
  InputBase,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import { createRef, useEffect, useRef, useState } from 'react';
import {
  Add,
  ArrowBack,
  ArrowLeft,
  Delete as DeleteIcon,
  EditNote,
} from '@mui/icons-material';
import NavigationIcon from '@mui/icons-material/Navigation';
import {
  Loading,
  useAuthState,
  useGetIdentity,
  useGetRecordId,
  useStore,
  useStoreContext,
} from 'react-admin';
import authProvider, { loggedUser } from '../../authProvider';
import {
  createNote,
  deleteNote,
  getNotes,
  updateNote,
  updateReadNote,
} from './notesApi';
import { useLocation, useNavigate } from 'react-router';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { formatDistanceStrict } from 'date-fns';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import NoteBox from './noteBox';
import { useNotesDrawer } from '../../hooks/useNotesDrawer';
import { useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

const styles = {
  floatIcon: {
    // right: '1.5em',
    // bottom: '.2em',
    // position: 'fixed',
  },
};

const notesEnabledPages = [
  'cards/*',
  'orders/*',
  'payees/*',
  'customers/*',
  'cards',
  'orders',
  'payees',
  'customers',
];
function isNotesEnabled(url: any) {
  return notesEnabledPages.some((pattern) => {
    if (pattern.endsWith('/*')) {
      return (
        url.startsWith(pattern.slice(0, -2)) && url.length > pattern.length - 2
      );
    }
    return url === pattern;
  });
}

const splitLocationPath = (path: string): string[] => {
  console.log(path);
  const [, section, itemId] = path.split('/');
  const route = [section, itemId !== 'create' ? itemId : ''];

  return isNotesEnabled(`${section}${itemId ? '/*' : ''}`) ? route : [];
};

export const Notes = () => {
  const location = useLocation();
  const [[section, itemId], setCurrentPath] = useStore('currentPath', []);
  const { isOpen, toggleNotesDrawer, closeNotesDrawer, isNewNoteOpen } =
    useNotesDrawer();

  const navigate = useNavigate();
  useEffect(() => {
    setCurrentPath(() => splitLocationPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.from === 'dashboard') {
      toggleNotesDrawer();
      navigate(location.pathname, { replace: true });
    }
  }, [location.state?.from]);

  return section ? (
    <>
      <IconButton
        // color="primary"
        aria-label='edit'
        sx={styles.floatIcon}
        onClick={toggleNotesDrawer}
      >
        {/* <Badge badgeContent={4} color="error" variant="standard"> */}
        <NoteAltIcon />
        {/* </Badge> */}
      </IconButton>
      <Drawer
        open={isOpen}
        onClose={closeNotesDrawer}
        anchor='right'
        PaperProps={{
          style: {
            borderTopLeftRadius: 10,
          },
        }}
      >
        <NotesSection isNewNoteOpen={isNewNoteOpen} />
      </Drawer>
    </>
  ) : null;
};

export const NotesSection = ({
  aside = false,
}: // isNewNoteOpen,
{
  aside?: boolean;
  isNewNoteOpen?: boolean;
}) => {
  const { openNotesDrawer, resetIsNewNoteOpen, isNewNoteOpen } =
    useNotesDrawer();
  console.log(isNewNoteOpen, 'isNewNoteOpen');

  const [[section, itemId], setCurrentPath] = useStore('currentPath', []);
  const [allNotes, setAllNotes] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const scrollRef = useRef();
  const pageSize = 25;
  const [pageDetails, setPageDetails] = useState<any>({});
  const [notesLoading, setNotesLoading] = useState(false);
  const unreadNotes = allNotes
    .filter((item: any) => {
      const readDone =
        item.createdBy?.userId == loggedUser.userId ||
        item.readBy?.some(({ user }: any) => user.userId == loggedUser.userId);
      return !readDone;
    })
    .map((item: any) => item._id);

  const _getNotes = () => {
    setNotesLoading(true);
    getNotes({
      section,
      itemId,
      currentPage: currentPage - 1,
      pageSize,
      read: aside ? 'false' : undefined,
    }).then((data: any) => {
      setAllNotes(
        isNewNoteOpen
          ? [
              {
                _id: -1,
                createdBy: {
                  firstName: loggedUser.firstName,
                  lastName: loggedUser.lastName,
                },
              },
              ,
              ...(data.data || []),
            ]
          : data.data || []
      );
      resetIsNewNoteOpen();

      setPageDetails({ total: data.total, range: data.range });
      setNotesLoading(false);
    });
  };

  useEffect(() => {
    if (aside && section && itemId) _getNotes();
    else if (!aside && section) _getNotes();
  }, [section, itemId, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setAllNotes([]);
  }, [section, itemId]);

  const isNewNoteExist = allNotes.some((i: any) => i._id == -1);

  const deleteNewlyAddedNote = () => {
    setAllNotes((allNotes: any) => allNotes.filter((i: any) => i._id !== -1));
  };

  const setAllReadDone = () => {
    updateReadNote({ noteIds: unreadNotes }).then(() => {
      _getNotes();
    });
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );

  return (
    <Card
      sx={{
        width: aside ? 'inherit' : isMobile ? '75vw' : '360px',
        padding: '1em 1em 0 1em',
        display: 'flex',
        flexDirection: 'column',
        height: aside ? 'auto' : 'calc(100vh - 1em)',
        borderWidth: aside ? 1 : 0,
        marginBottom: aside ? '1rem' : '0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Box>
            <Typography fontWeight={500} fontSize={18}>
              Notes{section ? ` (${section})` : ''}
            </Typography>
          </Box>
          {itemId && !aside && (
            <Typography
              variant='h6'
              fontWeight={400}
              fontSize={'.8em'}
              color={'text.secondary'}
            >
              ID:{itemId}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {aside && (
            <>
              <Tooltip title='Add Note'>
                <Fab
                  color='primary'
                  size='small'
                  onClick={() => openNotesDrawer(true)}
                >
                  <Add />
                </Fab>
              </Tooltip>
              <Tooltip title='Show All Notes'>
                <Fab
                  color='primary'
                  size='small'
                  onClick={() => openNotesDrawer()}
                >
                  <FormatListBulletedIcon />
                </Fab>
              </Tooltip>
            </>
          )}
          {!isNewNoteExist && unreadNotes.length > 0 && !aside && (
            <Tooltip title='Mark All as Read'>
              <Fab
                // startIcon={<Add />}
                // variant="contained"
                color='success'
                size='small'
                onClick={setAllReadDone}
              >
                <DoneAllIcon />
              </Fab>
            </Tooltip>
          )}
          {!isNewNoteExist && itemId && !aside && (
            <Tooltip title='New Note'>
              <Fab
                // startIcon={<Add />}
                // variant="contained"
                color='primary'
                size='small'
                onClick={() => {
                  setAllNotes([
                    {
                      _id: -1,
                      createdBy: {
                        firstName: loggedUser.firstName,
                        lastName: loggedUser.lastName,
                      },
                    },
                    ...allNotes,
                  ]);
                }}
              >
                <Add />
              </Fab>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          overflow: 'auto',
          scrollbarWidth: 'none',
          mt: 2,
          pb: 2,
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        ref={scrollRef}
      >
        {notesLoading ? (
          <>
            <Stack gap={3}>
              <Skeleton
                variant='rectangular'
                sx={{
                  minHeight: 150,
                  borderRadius: 0.5,
                }}
              />
              <Skeleton
                variant='rectangular'
                sx={{
                  minHeight: 150,
                  borderRadius: 0.5,
                }}
              />
              <Skeleton
                variant='rectangular'
                sx={{
                  minHeight: 150,
                  borderRadius: 0.5,
                }}
              />
              <Skeleton
                variant='rectangular'
                sx={{
                  minHeight: 150,
                  borderRadius: 0.5,
                }}
              />
            </Stack>
          </>
        ) : allNotes.length > 0 ? (
          <>
            {allNotes.map((note: any, index: number) => (
              <NoteBox
                note={note}
                index={index}
                key={note._id}
                callback={_getNotes}
                deleteNewlyAddedNote={deleteNewlyAddedNote}
                resetIsNewNoteOpen={resetIsNewNoteOpen}
              />
            ))}
            {/* <Button onClick={() => setCurrentPage((page) => page + 1)}>
              Show More
            </Button> */}
          </>
        ) : aside ? (
          <Box sx={{ textAlign: 'center' }}>No unread notes found</Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: '30vh',
            }}
          >
            <NoteAltIcon
              sx={{ height: '80px', width: '80px', color: 'text.light' }}
            />

            <Typography variant='h5' color={'text.light'}>
              No Notes Found
            </Typography>
          </Box>
        )}
      </Box>

      {pageDetails.total > 0 && !aside && (
        <Stack
          direction={'row'}
          spacing={1}
          sx={{ p: 2 }}
          alignItems={'center'}
          marginLeft={'auto'}
        >
          <Box sx={{ fontSize: '14px' }}>
            {pageDetails.range
              .split('-')
              .map((i: any) => parseInt(i) + 1)
              .join(' - ')}{' '}
            of {pageDetails.total}
          </Box>
          <Pagination
            size='small'
            // sx={{ p: 1 }}
            count={Math.ceil(pageDetails.total / pageSize)}
            // color="primary"
            page={currentPage}
            onChange={(e, value) => {
              setCurrentPage(value);
            }}
          />
        </Stack>
      )}
    </Card>
  );
};
